import { useCallback, useEffect } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions, messaging } from '@/config/firebase';
import { toast as sonnerToast } from 'sonner';

const updateFcmToken = httpsCallable<{ token: string; action: 'add' | 'remove' }, { success: boolean }>(firebaseFunctions, 'updateFcmToken');
const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return "Unknown notification setup error.";
};

export const usePushNotifications = () => {
  const navigate = useNavigate();
  const requestPermissionAndGetToken = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      sonnerToast.error("Notifications are not supported on this device.");
      return null;
    }
    if (!window.isSecureContext) {
      sonnerToast.info("Enable HTTPS to use push notifications.");
      return null;
    }
    if (!messaging) {
      sonnerToast.error("Notifications are not supported on this device.");
      return null;
    }

    try {
      const currentPermission = await Notification.requestPermission();

      if (currentPermission === 'granted') {
        console.log('Notification permission granted.');
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        if (!vapidKey) throw new Error("VAPID key is missing in client environment.");

        if (!('serviceWorker' in navigator)) {
          throw new Error("Service workers are unavailable in this browser.");
        }

        let swRegistration = await navigator.serviceWorker.getRegistration();
        if (!swRegistration) {
          swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
        swRegistration = await navigator.serviceWorker.ready;
        if (!swRegistration || !('pushManager' in swRegistration)) {
          throw new Error("Push manager is unavailable for service worker registration.");
        }

        const currentToken = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: swRegistration,
        });
        if (currentToken) {
          console.log('FCM Token received:', currentToken);
          await updateFcmToken({ token: currentToken, action: 'add' });
          sonnerToast.success("Notifications enabled!");
          return currentToken;
        } else {
          throw new Error("Could not get registration token.");
        }
      } else {
        console.log('Notification permission was denied.');
        sonnerToast.info("Notifications have not been enabled. You can enable them later in your browser settings.");
        return null;
      }
    } catch (err: unknown) {
      const message = toErrorMessage(err);
      console.error('An error occurred while enabling notifications.', err);
      sonnerToast.error(`Error enabling notifications: ${message}`);
      return null;
    }
  }, []);
  
  const setupForegroundListener = useCallback(() => {
    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('Foreground message received.', payload);
        sonnerToast.info(payload.notification?.title || 'New Notification', {
          description: payload.notification?.body,
          action: {
            label: 'View',
            onClick: () => {
              if (payload.fcmOptions?.link) {
                navigate(payload.fcmOptions.link);
              }
            },
          },
        });
      });
      return unsubscribe;
    }
    return () => {};
  }, [navigate]);

  // Effect to set up the listener once when the hook is used by an authenticated user.
  useEffect(() => {
    const unsubscribe = setupForegroundListener();
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [setupForegroundListener]);

  return {
    requestPermissionAndGetToken,
  };
};
